import React from 'react';
import InsuranceUploadConfirmationPop from './popups/InsuranceUploadConfirmationPop';
import InsuranceUploadProgressDisplay from './progress/InsuranceUploadProgressDisplay';
import ConfirmDialog from './dialogs/ConfirmDialog';
import iconTitle from '../assets/img/icon-square.svg';
import { INSURANCE_TYPE } from '../utils/insuranceTypeUtils';

// 保険会社申請アップロードの共通コンテンツコンポーネント
const InsuranceUploadContent = ({
  // 状態
  selectedCompanies,
  agencyInsuranceData,
  insuranceFlags,
  uploadFiles,
  showConfirmationPopup,
  missingFiles,
  isSubmitting,
  submissionProgress,
  confirmDialog,
  expandedCompanies,
  
  // ハンドラー関数
  handleFileSelect,
  triggerFileSelect,
  removeFile,
  closeConfirmationPopup,
  confirmSubmission,
  handleSubmit,
  handleReturn,
  handleProgressCancel,
  handleCompleteNavigate,
  handleConfirmDialogConfirm,
  handleConfirmDialogCancel,
  toggleCompanyExpansion
}) => {
  const lifeCompanies = React.useMemo(
    () => selectedCompanies.filter(company => company.type === INSURANCE_TYPE.LIFE),
    [selectedCompanies]
  );
  const sonpoCompanies = React.useMemo(
    () => selectedCompanies.filter(company => company.type === INSURANCE_TYPE.SONPO),
    [selectedCompanies]
  );

  const getCompanyFlagStatus = React.useCallback((company, flagKey) => {
    if (company && typeof company === 'object' && company[flagKey] !== undefined) {
      return !!company[flagKey];
    }

    const companyId = typeof company === 'object' && company !== null ? company.id : null;
    const companyName = typeof company === 'object' && company !== null ? company.name : company;

    if (companyId && insuranceFlags && Object.prototype.hasOwnProperty.call(insuranceFlags, companyId)) {
      const flagInfo = insuranceFlags[companyId];
      if (flagInfo && Object.prototype.hasOwnProperty.call(flagInfo, flagKey)) {
        return !!flagInfo[flagKey];
      }
    }

    const relationData = (agencyInsuranceData || []).find(item =>
      (companyId && item.id === companyId) || item.name === companyName
    );

    if (relationData && relationData[flagKey] !== undefined) {
      return !!relationData[flagKey];
    }

    return false;
  }, [agencyInsuranceData, insuranceFlags]);

  const getCompanyHasHikiuke = React.useCallback(
    (company) => getCompanyFlagStatus(company, 'has_hikiuke'),
    [getCompanyFlagStatus]
  );

  const getCompanyHasHandbook = React.useCallback(
    (company) => getCompanyFlagStatus(company, 'has_handbook'),
    [getCompanyFlagStatus]
  );
  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="page-header">
          <div className="title-section">
            <div className="icon-square">
              <img src={iconTitle} alt="タイトルアイコン" />
            </div>
            <h1>保険会社申請</h1>
          </div>
        </div>
        
        <div className="insurance-companies-container">
          {/* 保険会社選択エリア */}
          <div className='insurance-checkbox-container-wrapper insurance-checkbox-container-wrapper--ple-space'>
            <div className="insurance-checkbox-container">
              <h2>生命保険会社</h2>
              {/* 選択された保険会社ごとにファイルアップロードエリアを表示 */}
              {lifeCompanies.map((company, companyIndex) => {
                // companyがオブジェクトかどうかチェック
                const companyName = typeof company === 'object' && company !== null ? company.name : company;
                const isLinked = typeof company === 'object' && company !== null ? company.isLinked : false;
                const hasHandbook = getCompanyHasHandbook(company);
                const hasHikiuke = getCompanyHasHikiuke(company);
                
                // アコーディオンの展開状態を判定
                // expandedCompaniesが初期化されていない場合は、isLinkedに基づいて初期状態を決定
                const isExpanded = expandedCompanies.hasOwnProperty(companyName) 
                  ? expandedCompanies[companyName] 
                  : !isLinked; // isLinked: true → 閉じた状態（false）, isLinked: false → 展開状態（true）
                
                return (
                  <div key={companyIndex} className="insurance-company-upload-section">
                    {/* 保険会社名と展開ボタン */}
                    <div className="insurance-company-header" onClick={() => toggleCompanyExpansion(companyName)}>
                      <div className="company-info">
                        <h3 className="insurance-company-name">{companyName}</h3>
                        {/* 紐づき状態のバッジ表示 */}
                        <span className={`status-badge ${isLinked ? 'linked' : 'new'}`}>
                          {isLinked ? '承認済' : '新規申請'}
                        </span>
                      </div>
                      <button 
                        type="button" 
                        className={`company-expand-button ${isExpanded ? 'expanded' : ''}`}
                        disabled={isSubmitting}
                        aria-label={isExpanded ? '縮小' : '展開'}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* ファイルアップロードエリア（展開時のみ表示） */}
                    {isExpanded && (
                      <div className="insurance-upload-area life-insurance-layout">
                        {/* 上の行: 新契約関連と保全・収納・支払関連 */}
                        <div className="life-insurance-grid-top">
                          {/* 新契約関連 */}
                          <div className="upload-category">
                            <div className="upload-section">
                              <div className="upload-label">新契約関連</div>
                              <div className="upload-control">
                                <input
                                  type="file"
                                  id={`newContract-${companyName}`}
                                  onChange={(e) => handleFileSelect(companyName, 'newContract', e)}
                                  style={{ display: 'none' }}
                                  accept=".pdf,.doc,.docx"
                                  multiple
                                  disabled={isSubmitting}
                                />
                                <div className="upload-input-group">
                                  <input
                                    type="text"
                                    className="upload-input"
                                    value="ファイルを選択"
                                    readOnly
                                    onClick={() => triggerFileSelect(`newContract-${companyName}`)}
                                    disabled={isSubmitting}
                                  />
                                  <button
                                    type="button"
                                    className="file-select-button"
                                    onClick={() => triggerFileSelect(`newContract-${companyName}`)}
                                    disabled={isSubmitting}
                                  >
                                    ファイル選択
                                  </button>
                                </div>
                              </div>
                              {uploadFiles[companyName]?.newContract && (
                                <div className="selected-files">
                                  {uploadFiles[companyName].newContract.map((fileItem) => (
                                    <div key={fileItem.id} className="selected-file-item">
                                      <span className="file-name">{fileItem.file.name}</span>
                                      <button
                                        type="button"
                                        className="remove-file-button"
                                        onClick={() => removeFile(companyName, 'newContract', fileItem.id)}
                                        disabled={isSubmitting}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 保全・収納・支払関連セクション */}
                          <div className="upload-category">
                            <div className="upload-section">
                              <div className="upload-label">保全・収納・支払関連</div>
                              <div className="upload-control">
                                <input
                                  type="file"
                                  id={`maintenance-${companyName}`}
                                  onChange={(e) => handleFileSelect(companyName, 'maintenance', e)}
                                  style={{ display: 'none' }}
                                  accept=".pdf,.doc,.docx"
                                  multiple
                                  disabled={isSubmitting}
                                />
                                <div className="upload-input-group">
                                  <input
                                    type="text"
                                    className="upload-input"
                                    value="ファイルを選択"
                                    readOnly
                                    onClick={() => triggerFileSelect(`maintenance-${companyName}`)}
                                    disabled={isSubmitting}
                                  />
                                  <button
                                    type="button"
                                    className="file-select-button"
                                    onClick={() => triggerFileSelect(`maintenance-${companyName}`)}
                                    disabled={isSubmitting}
                                  >
                                    ファイル選択
                                  </button>
                                </div>
                              </div>
                              {uploadFiles[companyName]?.maintenance && (
                                <div className="selected-files">
                                  {uploadFiles[companyName].maintenance.map((fileItem) => (
                                    <div key={fileItem.id} className="selected-file-item">
                                      <span className="file-name">{fileItem.file.name}</span>
                                      <button
                                        type="button"
                                        className="remove-file-button"
                                        onClick={() => removeFile(companyName, "maintenance", fileItem.id)}
                                        disabled={isSubmitting}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 下の行: 引受関連 */}
                        {hasHikiuke && (
                          <div className="life-insurance-grid-bottom">
                            <div className="upload-category">
                              <div className="upload-section">
                                <div className="upload-label">引受目安</div>
                                <div className="upload-control">
                                  <input
                                    type="file"
                                    id={`hikiuke-${companyName}`}
                                    onChange={(e) => handleFileSelect(companyName, "hikiuke", e)}
                                    style={{ display: "none" }}
                                    accept=".pdf,.doc,.docx"
                                    multiple
                                    disabled={isSubmitting}
                                  />
                                  <div className="upload-input-group">
                                    <input
                                      type="text"
                                      className="upload-input"
                                      value="ファイルを選択"
                                      readOnly
                                      onClick={() => triggerFileSelect(`hikiuke-${companyName}`)}
                                      disabled={isSubmitting}
                                    />
                                    <button
                                      type="button"
                                      className="file-select-button"
                                      onClick={() => triggerFileSelect(`hikiuke-${companyName}`)}
                                      disabled={isSubmitting}
                                    >
                                      ファイル選択
                                    </button>
                                  </div>
                                </div>
                                {uploadFiles[companyName]?.hikiuke && (
                                  <div className="selected-files">
                                    {uploadFiles[companyName].hikiuke.map((fileItem) => (
                                      <div key={fileItem.id} className="selected-file-item">
                                        <span className="file-name">{fileItem.file.name}</span>
                                        <button
                                          type="button"
                                          className="remove-file-button"
                                          onClick={() => removeFile(companyName, "hikiuke", fileItem.id)}
                                          disabled={isSubmitting}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <h2>損害保険会社</h2>
              {/* 選択された保険会社ごとにファイルアップロードエリアを表示 */}
              {sonpoCompanies.map((company, companyIndex) => {
                // companyがオブジェクトかどうかチェック
                const companyName = typeof company === 'object' && company !== null ? company.name : company;
                const isLinked = typeof company === 'object' && company !== null ? company.isLinked : false;
                const hasHandbook = getCompanyHasHandbook(company);
                const hasUploadSection = hasHandbook;
                
                // アコーディオンの展開状態を判定
                // expandedCompaniesが初期化されていない場合は、isLinkedに基づいて初期状態を決定
                const isExpanded = expandedCompanies.hasOwnProperty(companyName) 
                  ? expandedCompanies[companyName] 
                  : !isLinked; // isLinked: true → 閉じた状態（false）, isLinked: false → 展開状態（true）
                
                return (
                  <div key={companyIndex} className="insurance-company-upload-section">
                    {/* 保険会社名と展開ボタン */}
                    <div className="insurance-company-header" onClick={() => toggleCompanyExpansion(companyName)}>
                      <div className="company-info">
                        <h3 className="insurance-company-name">{companyName}</h3>
                        {/* 紐づき状態のバッジ表示 */}
                        <span className={`status-badge ${isLinked ? 'linked' : 'new'}`}>
                          {isLinked ? '承認済' : '新規申請'}
                        </span>
                      </div>
                      <button 
                        type="button" 
                        className={`company-expand-button ${isExpanded ? 'expanded' : ''}`}
                        disabled={isSubmitting}
                        aria-label={isExpanded ? '縮小' : '展開'}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* ファイルアップロードエリア（展開時のみ表示） */}
                    {isExpanded && (
                      <>
                        {hasUploadSection ? (
                          <div className="insurance-upload-area">
                            {/* ハンドブック */}
                            <div className="upload-category">
                              <div className="upload-section">
                                <div className="upload-label">ハンドブック</div>
                                <div className="upload-control">
                                  <input
                                    type="file"
                                    id={`handbook-${companyName}`}
                                    onChange={(e) => handleFileSelect(companyName, 'handbook', e)}
                                    style={{ display: 'none' }}
                                    accept=".pdf,.doc,.docx"
                                    multiple
                                    disabled={isSubmitting}
                                  />
                                  <div className="upload-input-group">
                                    <input
                                      type="text"
                                      className="upload-input"
                                      value="ファイルを選択"
                                      readOnly
                                      onClick={() => triggerFileSelect(`handbook-${companyName}`)}
                                      disabled={isSubmitting}
                                    />
                                    <button
                                      type="button"
                                      className="file-select-button"
                                      onClick={() => triggerFileSelect(`handbook-${companyName}`)}
                                      disabled={isSubmitting}
                                    >
                                      ファイル選択
                                    </button>
                                  </div>
                                </div>
                                
                                {/* 選択されたファイル一覧 */}
                                {uploadFiles[companyName]?.handbook && (
                                  <div className="selected-files">
                                    {uploadFiles[companyName].handbook.map((fileItem) => (
                                      <div key={fileItem.id} className="selected-file-item">
                                        <span className="file-name">{fileItem.file.name}</span>
                                        <button 
                                          type="button" 
                                          className="remove-file-button"
                                          onClick={() => removeFile(companyName, 'handbook', fileItem.id)}
                                          disabled={isSubmitting}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="insurance-upload-area">
                            <div className="upload-category">
                              <div className="upload-section">
                                <div className="upload-label">申請可能な文書がありません</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* ボタンエリア */}
          <div className="action-buttons c-max-mini c-m-center">
            <button 
              type="button" 
              className="button button-secondary button-medium" 
              onClick={handleReturn}
              disabled={isSubmitting}
            >
              戻る
            </button>
            <button 
              className="btn btn-primary button-medium" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              申請する
            </button>
          </div>
        </div>
      </div>

      {/* 確認ポップアップコンポーネント */}
      <InsuranceUploadConfirmationPop
        showConfirmationPopup={showConfirmationPopup}
        missingFiles={missingFiles}
        selectedCompanies={selectedCompanies}
        uploadFiles={uploadFiles}
        insuranceFlags={insuranceFlags}
        onClose={closeConfirmationPopup}
        onConfirm={confirmSubmission}
        isSubmitting={isSubmitting}
      />

      {/* プログレス表示コンポーネント */}
      <InsuranceUploadProgressDisplay
        isVisible={submissionProgress.isProcessing}
        stage={submissionProgress.stage}
        message={submissionProgress.message}
        detail={submissionProgress.detail}
        progress={submissionProgress.progress}
        uploadProgress={submissionProgress.uploadProgress}
        timeRemaining={submissionProgress.timeRemaining}
        onCancel={submissionProgress.stage === 'uploading' ? handleProgressCancel : null}
        onComplete={handleCompleteNavigate}
        size="large"
        theme="default"
      />

      {/* カスタム確認ダイアログ */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={handleConfirmDialogConfirm}
        onCancel={handleConfirmDialogCancel}
        theme={confirmDialog.theme}
        size="medium"
        confirmText="OK"
        cancelText="キャンセル"
        showCancelButton={true}
        closeOnOverlayClick={true}
      />
    </div>
  );
};

export default InsuranceUploadContent;
